import { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

interface ImageSliderProps {
  images: string[];
  height?: number | string; // when 'auto', use natural image height
}

export const ImageSlider = ({ images, height = 'auto' }: ImageSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchDeltaX, setTouchDeltaX] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex(prevIndex => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex(prevIndex => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (e.touches && e.touches.length > 0) {
      setTouchStartX(e.touches[0].clientX);
      setTouchDeltaX(0);
    }
  };

  const onTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (touchStartX !== null && e.touches && e.touches.length > 0) {
      const currentX = e.touches[0].clientX;
      setTouchDeltaX(currentX - touchStartX);
    }
  };

  const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = () => {
    const threshold = 50; // px
    if (touchDeltaX > threshold) {
      goToPrevious();
    } else if (touchDeltaX < -threshold) {
      goToNext();
    }
    setTouchStartX(null);
    setTouchDeltaX(0);
  };

  if (images.length === 0) {
    return (
      <Box 
        sx={{
          width: '100%',
          ...(height !== 'auto' ? { height } : {}),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.paper',
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box 
          component="img"
          src="https://placehold.co/600x400/efefef/999999?text=No+Image"
          alt="No image available"
          sx={{
            width: '100%',
            height: height !== 'auto' ? '100%' : 'auto',
            objectFit: 'contain',
          }}
        />
      </Box>
    );
  }

  return (
    <Box 
      className="slider-buttons"
      sx={{
        position: 'relative',
        width: '100%',
        ...(height !== 'auto' ? { height } : {}),
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Main Image */}
      <Box
        component="img"
        src={images[currentIndex]}
        alt={`Product view ${currentIndex + 1}`}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = 'https://placehold.co/600x400/ef4444/ffffff?text=Image+Not+Found';
        }}
        sx={{
          width: '100%',
          height: height !== 'auto' ? '100%' : 'auto',
          objectFit: 'contain',
          backgroundColor: 'background.paper',
        }}
      />

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <IconButton
            onClick={goToPrevious}
            sx={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
            size="large"
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={goToNext}
            sx={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
            size="large"
          >
            <ChevronRight />
          </IconButton>
        </>
      )}

      {/* Dots Indicator */}
      {images.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {images.map((_, index) => (
            <Box
              key={index}
              className="slider-dot"
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: currentIndex === index ? 'var(--blue)' : '#cccccc',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'var(--blue)',
                  transform: 'scale(1.2)',
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
